import React, { ReactNode, useContext, useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'

import { ONGOING_PAYMENT_PATH } from '../pages/OngoingPayments/constants'
import { TRANSACTION_PAGE_PATH } from '../pages/Transactions/constants'
import { setPaginationQueryParams } from '../util/pagination/setPaginationQueryParams'
import PaginationContext, {
  FilterValues,
  PaginationContextType,
  PaginationData,
} from '../context/PaginationContext'
import { AuthContext } from '../context/AuthContext'
import { deObfuscateData } from '../util/helpers'

const PaginationPaths = [
  {
    path: ONGOING_PAYMENT_PATH,
    key: 'ongoing',
  },
  {
    path: TRANSACTION_PAGE_PATH,
    key: 'transactions',
  },
]

const findKeyByPath = (pathname: string) => {
  const match = PaginationPaths.find(p => p.path === pathname)
  if (match) {
    return match.key
  }

  return ''
}

export const PaginationProvider: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const { token } = useContext(AuthContext)

  const [paginationState, setPaginationState] = React.useState<
    Record<string, PaginationData>
  >({})
  const history = useHistory()
  const location = useLocation()
  const queryString = location.search

  const setPaginationData = (data: PaginationData) =>
    setPaginationState(prevState => ({
      ...prevState,
      [findKeyByPath(location.pathname)]: data,
    }))

  const getPaginationData = (key: string): PaginationData =>
    paginationState[key]

  const updateQueryParams = (data: PaginationData) =>
    setPaginationQueryParams(
      {
        page: data.page,
        searchQuery: data.searchQuery,
        size: data.size,
        sort: data.sort,
        filters: data.filters,
      },
      history,
    )

  const resetPaginationData = () => {
    setPaginationState({})
  }

  const parseFiltersFromString = (filterString: string): FilterValues => {
    const filters: FilterValues = {}
    filterString.split(';').forEach(pair => {
      const [key, value] = pair.split(':')
      if (key && value) {
        filters[key] = value
      }
    })

    return filters
  }

  useEffect(() => {
    if (PaginationPaths.find(p => location.pathname === p.path)) {
      const paginationData = getPaginationData(findKeyByPath(location.pathname))
      if (location.search) {
        const queryParams = new URLSearchParams(queryString)
        const page = parseInt(queryParams.get('page') || '', 10) || 0
        const size = parseInt(queryParams.get('size') || '', 10) || 10
        const sort = queryParams.get('sort') || ''
        const searchQuery =
          deObfuscateData(queryParams.get('searchQuery')) || ''
        const filters = parseFiltersFromString(queryParams.get('filters') || '')

        setPaginationData({
          ...paginationData,
          page,
          searchQuery,
          size,
          sort,
          filters,
        })
      } else if (paginationData) {
        updateQueryParams({
          ...getPaginationData(findKeyByPath(location.pathname)),
        })
      }
    }
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!token) {
      setPaginationState({})
    }
  }, [token])

  const value: PaginationContextType = useMemo(
    () => ({
      setPaginationData,
      getPaginationData,
      updateQueryParams,
      resetPaginationData,
    }),
    [paginationState],
  )

  return (
    <PaginationContext.Provider value={value}>
      {children}
    </PaginationContext.Provider>
  )
}

export const usePagination = () => {
  const location = useLocation()
  const context = useContext(PaginationContext)

  const paginationData = context?.getPaginationData(
    findKeyByPath(location.pathname),
  )

  return { ...context, paginationData }
}
